import { createSelector } from '@reduxjs/toolkit';

import type { IAgentContract } from '@/api/types';
import type { RootState } from '@/store';
import type { IContract, IContractState } from '@/store/types';

import { reinstateAgent, removeAgent, suspendAgent, upsertResignContract } from '@/store/slices/contractSlice';

/**
 * SP
 */
export const selectContractSchedules = (state: RootState): IContractState['contractSchedules'] =>
  state.contract.contractSchedules;
export const selectContractRequirementsAll = (state: RootState): IContractState['contractRequirementsAll'] =>
  state.contract.contractRequirementsAll;
export const selectContractRequirementsUnmet = (state: RootState): IContractState['contractRequirementsUnmet'] =>
  state.contract.contractRequirementsUnmet;
export const selectContractAttendance = (state: RootState): IContractState['contractAttendance'] =>
  state.contract.contractAttendance;
export const selectContractAdherence = (state: RootState): IContractState['contractAdherence'] =>
  state.contract.contractAdherence;
export const selectContractAdherenceReason = (state: RootState): IContractState['contractAdherenceReason'] =>
  state.contract.contractAdherenceReason;
// TODO: create selector to filter resigns (by contractId)
export const selectContractResignationPendingList = (
  state: RootState,
): IContractState['contractResignationPendingList'] => state.contract.contractResignationPendingList;
export const selectResignContractModalData = (state: RootState): IContractState['resignContractModalData'] =>
  state.contract.resignContractModalData;
export const selectContractPeaks = (state: RootState): IContractState['peakHours'] => state.contract.peakHours;
export const selectTimeOffsList = (state: RootState): IContractState['timeOffsList'] => state.contract.timeOffsList;
export const selectTimeOffsRaw = (state: RootState): IContractState['timeOffsRaw'] => state.contract.timeOffsRaw;
export const selectContractsList = (state: RootState): IContractState['contractsList'] => state.contract.contractsList;
export const selectActiveContractsList = (state: RootState): IContract[] =>
  state.contract.contractsList?.filter(item => item.isCurrentContract && !item.resTermLiftSuspension) ?? [];
export const selectAgentContracts = (state: RootState): IAgentContract[] => state.auth.user?.agentContracts ?? [];
export const selectRestermReasonList = (state: RootState): IContractState['restermReasonsList'] =>
  state.contract.restermReasonsList;
export const selectContractDetails = (state: RootState): IContractState['contractDetails'] =>
  state.contract.contractDetails;

/**
 * SU
 */
export const selectClientContractRequirementsAll = (
  state: RootState,
): IContractState['client']['contractRequirementsAll'] => state.contract.client.contractRequirementsAll;
export const selectClientContractSchedules = (state: RootState): IContractState['client']['contractSchedules'] =>
  state.contract.client.contractSchedules;
export const selectClientContractsList = (state: RootState): IContractState['client']['contractsList'] =>
  state.contract.client.contractsList;
export const selectClientContractDetails = (state: RootState): IContractState['client']['contractDetails'] =>
  state.contract.client.contractDetails;
export const selectClientContractAttendance = (state: RootState): IContractState['client']['contractAttendance'] =>
  state.contract.client.contractAttendance;
export const selectClientContractAdherence = (state: RootState): IContractState['client']['contractAdherence'] =>
  state.contract.client.contractAdherence;
export const selectClientContractAdherenceReason = (
  state: RootState,
): IContractState['client']['contractAdherenceReason'] => state.contract.client.contractAdherenceReason;
export const selectClientRestermReasons = (state: RootState): IContractState['client']['restermReasonsList'] =>
  state.contract.client.restermReasonsList;
export const selectContractAgents = (state: RootState): IContractState['client']['contractAgents'] =>
  state.contract.client.contractAgents;
export const selectCurrentAgent = (state: RootState): IContractState['client']['currentAgent'] =>
  state.contract.client.currentAgent;

/**
 * Meta
 */
export const selectPending = (state: RootState): boolean => Boolean(state.contract.meta.pending.length);
export const selectIsPendingAction =
  (actionType: string) =>
  (state: RootState): boolean =>
    state.contract.meta.pending.some(action => action === actionType);

export const selectIsPendingRestermActions = createSelector(
  selectIsPendingAction(removeAgent.typePrefix),
  selectIsPendingAction(suspendAgent.typePrefix),
  selectIsPendingAction(reinstateAgent.typePrefix),
  selectIsPendingAction(upsertResignContract.typePrefix),
  (isPendingRemove, isPendingSuspend, isPendingReinstate, isPendingResignation) =>
    isPendingRemove || isPendingSuspend || isPendingReinstate || isPendingResignation,
);
